import { useState, useEffect } from 'react';
import { ArrowPathIcon, CheckCircleIcon, EnvelopeIcon, ExclamationTriangleIcon, MagnifyingGlassIcon, PhoneIcon, UserIcon, XCircleIcon } from '@heroicons/react/24/solid';

type Volunteer = {
  id: string;
  name: string;
  email: string;
  phone?: string;
  joinDate: string;
  status: 'active' | 'inactive' | 'pending';
  skills: string[];
  hours: number;
  avatar?: string;
  upcomingShifts?: {
    id: string;
    date: string;
    duration: number;
    role: string;
  }[];
  applicationDate?: Date; // Date the volunteer applied (for 30-day tracking)
};

export default function Volunteers() {
  const [volunteers, setVolunteers] = useState<Volunteer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [processingIds, setProcessingIds] = useState<Record<string, boolean>>({});
  
  useEffect(() => {
    fetchVolunteers();
  }, []);
  
  const fetchVolunteers = async () => {
    setIsLoading(true);
    try {
      // In a real app, this would be an API call
      // const data = await getVolunteers();
      
      // Mock data for demonstration with application dates for 30-day window tracking
      const mockVolunteers: Volunteer[] = [
        {
          id: 'v1001',
          name: 'John Smith',
          email: 'john.smith@example.com',
          phone: '(555) 123-4567',
          joinDate: '2024-01-15T00:00:00Z',
          applicationDate: new Date(Date.now() - 1000 * 60 * 60 * 24 * 5), // 5 days ago
          status: 'active',
          skills: ['Food Sorting', 'Delivery', 'Event Planning'],
          hours: 45,
          avatar: 'https://via.placeholder.com/150',
          upcomingShifts: [
            {
              id: 's1001',
              date: '2025-05-10T09:00:00Z',
              duration: 3,
              role: 'Food Distribution'
            }
          ]
        },
        {
          id: 'v1002',
          name: 'Sarah Johnson',
          email: 'sarah.j@example.com',
          phone: '(555) 987-6543',
          joinDate: '2024-02-22T00:00:00Z',
          applicationDate: new Date(Date.now() - 1000 * 60 * 60 * 24 * 12), // 12 days ago
          status: 'active',
          skills: ['Administration', 'Fundraising', 'Social Media'],
          hours: 32,
          avatar: 'https://via.placeholder.com/150',
          upcomingShifts: [
            {
              id: 's1002',
              date: '2025-05-12T14:00:00Z',
              duration: 2,
              role: 'Office Support'
            }
          ]
        },
        {
          id: 'v1003',
          name: 'Michael Chen',
          email: 'michael.c@example.com',
          joinDate: '2024-03-10T00:00:00Z',
          applicationDate: new Date(Date.now() - 1000 * 60 * 60 * 24 * 18), // 18 days ago
          status: 'pending',
          skills: ['Food Preparation', 'Inventory Management'],
          hours: 0,
          avatar: 'https://via.placeholder.com/150'
        },
        {
          id: 'v1004',
          name: 'Emily Rodriguez',
          email: 'emily.r@example.com',
          phone: '(555) 234-5678',
          joinDate: '2023-11-05T00:00:00Z',
          applicationDate: new Date(Date.now() - 1000 * 60 * 60 * 24 * 25), // 25 days ago
          status: 'inactive',
          skills: ['Event Planning', 'Delivery', 'Food Sorting'],
          hours: 78,
          avatar: 'https://via.placeholder.com/150'
        },
        {
          id: 'v1005',
          name: 'David Wilson',
          email: 'david.w@example.com',
          phone: '(555) 876-5432',
          joinDate: '2024-04-18T00:00:00Z',
          applicationDate: new Date(Date.now() - 1000 * 60 * 60 * 24 * 28), // 28 days ago (almost expired)
          status: 'active',
          skills: ['Delivery', 'Heavy Lifting', 'Driving'],
          hours: 12,
          avatar: 'https://via.placeholder.com/150',
          upcomingShifts: [
            {
              id: 's1003',
              date: '2025-05-08T10:00:00Z',
              duration: 4,
              role: 'Delivery Driver'
            },
            {
              id: 's1004',
              date: '2025-05-15T10:00:00Z',
              duration: 4,
              role: 'Delivery Driver'
            }
          ]
        },
      ];
      
      setVolunteers(mockVolunteers);
    } catch (error) {
      console.error('Error fetching volunteers:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const getStatusBadge = (status: Volunteer['status']) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircleIcon className="h-4 w-4 mr-1" />
            Confirmed
          </span>
        );
      case 'inactive':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircleIcon className="h-4 w-4 mr-1" />
            Inactive
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
            Pending
          </span>
        );
      default:
        return null;
    }
  };
  
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    }).format(date);
  };
  
  // Calculate days remaining in the 30-day application window
  const getDaysRemaining = (applicationDate?: Date) => {
    if (!applicationDate) return 30;
    
    const now = new Date();
    const diffTime = 30 - Math.floor((now.getTime() - applicationDate.getTime()) / (1000 * 60 * 60 * 24));
    return Math.max(0, diffTime);
  };
  
  // Check if an application is expiring soon (less than 7 days left)
  const isExpiringSoon = (applicationDate?: Date) => {
    const daysRemaining = getDaysRemaining(applicationDate);
    return daysRemaining > 0 && daysRemaining <= 7;
  };
  
  // Check if an application is expired
  const isExpired = (applicationDate?: Date) => {
    return getDaysRemaining(applicationDate) === 0;
  };
  
  // No longer needed - shift date formatting function removed
  
  // Handle volunteer approval
  const handleApprove = async (volunteer: Volunteer) => {
    try {
      setProcessingIds(prev => ({ ...prev, [volunteer.id]: true }));
      
      // In a real app, this would be an API call to approve the volunteer
      // await approveVolunteer(volunteer.id);
      
      // Simulate API call with timeout
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Remove the volunteer from the list once approved
      setVolunteers(prev => prev.filter(v => v.id !== volunteer.id));
      
    } catch (error) {
      console.error('Error approving volunteer:', error);
    } finally {
      setProcessingIds(prev => ({ ...prev, [volunteer.id]: false }));
    }
  };
  
  // Handle volunteer rejection
  const handleReject = async (volunteer: Volunteer) => {
    try {
      setProcessingIds(prev => ({ ...prev, [volunteer.id]: true }));
      
      // In a real app, this would be an API call to reject the volunteer
      // await rejectVolunteer(volunteer.id);
      
      // Simulate API call with timeout
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Remove the volunteer from the list once rejected
      setVolunteers(prev => prev.filter(v => v.id !== volunteer.id));
      
    } catch (error) {
      console.error('Error rejecting volunteer:', error);
    } finally {
      setProcessingIds(prev => ({ ...prev, [volunteer.id]: false }));
    }
  };
  
  const filteredVolunteers = volunteers.filter(volunteer => {
    // Apply search filter
    if (searchTerm && !volunteer.name.toLowerCase().includes(searchTerm.toLowerCase()) && !volunteer.email.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    
    // Apply status filter
    if (statusFilter !== 'all' && volunteer.status !== statusFilter) {
      return false;
    }
    
    // Only show volunteers that applied within the last 30 days
    if (!volunteer.applicationDate) {
      return false;
    }
    
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return volunteer.applicationDate >= thirtyDaysAgo;
  });
  
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="space-y-6">
          <div className="flex items-center">
            <h1 className="text-xl font-medium text-navy-blue">Volunteers</h1>
          </div>
          <div className="space-y-2">
            {/* Filters and Actions */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
              <div className="flex items-center">
                <span className="ml-2 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  {volunteers.length}
                </span>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 w-full sm:w-auto">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search volunteers..."
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-sky-blue focus:border-sky-blue sm:text-sm"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <select
                className="block w-full sm:w-auto pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-sky-blue focus:border-sky-blue sm:text-sm rounded-md"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="active">Confirmed</option>
              </select>
              <button
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-blue"
                onClick={fetchVolunteers}
              >
                <ArrowPathIcon className="h-5 w-5 mr-2" />
                Refresh
              </button>
              
            </div>
          </div>
          
          {/* Volunteers List */}
          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-sky-blue"></div>
            </div>
          ) : filteredVolunteers.length > 0 ? (
            <div className="bg-white shadow-md rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Volunteer
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contact
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Hours
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Application
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredVolunteers.map((volunteer) => (
                      <tr key={volunteer.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              {volunteer.avatar ? (
                                <img 
                                  className="h-10 w-10 rounded-full" 
                                  src={volunteer.avatar} 
                                  alt={volunteer.name} 
                                />
                              ) : (
                                <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                  <UserIcon className="h-6 w-6 text-gray-400" />
                                </div>
                              )}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{volunteer.name}</div>
                              <div className="text-sm text-gray-500">Joined {formatDate(volunteer.joinDate)}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 flex items-center">
                            <EnvelopeIcon className="h-4 w-4 mr-1 text-gray-400" />
                            {volunteer.email}
                          </div>
                          {volunteer.phone && (
                            <div className="text-sm text-gray-500 flex items-center">
                              <PhoneIcon className="h-4 w-4 mr-1 text-gray-400" />
                              {volunteer.phone}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(volunteer.status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {volunteer.hours} hours
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {volunteer.applicationDate && (
                            <div>
                              {isExpired(volunteer.applicationDate) ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                  <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                                  Expired
                                </span>
                              ) : isExpiringSoon(volunteer.applicationDate) ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                  <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                                  Expires in {getDaysRemaining(volunteer.applicationDate)} days
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  {getDaysRemaining(volunteer.applicationDate)} days remaining
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          {!isExpired(volunteer.applicationDate) && (
                            <div className="flex justify-end space-x-2">
                              <button 
                                className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={() => handleApprove(volunteer)}
                                disabled={processingIds[volunteer.id]}
                              >
                                {processingIds[volunteer.id] ? (
                                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                ) : (
                                  <CheckCircleIcon className="-ml-0.5 mr-1 h-4 w-4" />
                                )}
                                {processingIds[volunteer.id] ? 'Processing...' : 'Approve'}
                              </button>
                              <button 
                                className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={() => handleReject(volunteer)}
                                disabled={processingIds[volunteer.id]}
                              >
                                {processingIds[volunteer.id] ? (
                                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                ) : (
                                  <XCircleIcon className="-ml-0.5 mr-1 h-4 w-4" />
                                )}
                                {processingIds[volunteer.id] ? 'Processing...' : 'Reject'}
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-md p-6 text-center">
              <p className="text-gray-500">No volunteers found.</p>
              {(searchTerm || statusFilter !== 'all') && (
                <button
                  className="mt-2 text-sky-blue hover:text-teal"
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('all');
                  }}
                >
                  Clear filters
                </button>
              )}
            </div>
          )}
          </div>
        </div>
      </div>
  );
}
